<template>
  <MarketWrapper
    :initial-fields="initialFields"
    :title-hero="titlePage"
    :image-hero="heroImage"
    :title-page="titlePage"
    :icon="icon"
    :tabs="tabs"
    has-bookable-demo
    @active-tab-changed="setActiveTab"
  >
    <template #methodology>
      <ButtonDownload
        data-testid="download-button"
        class="mx-0 w-full text-sm font-semibold leading-5 xl:max-w-fit"
        variant="clean"
        :text="downloadText"
        :download-file="file"
      />
    </template>
    <div class="relative pt-6">
      <slot />
    </div>
  </MarketWrapper>
</template>

<script setup lang="ts">
import ButtonDownload from '~/components/shared/common/ButtonDownload.vue'
import MarketWrapper from '~/components/shared/services/MarketWrapper.vue'
import { useScrollPosition } from '~/composables/useScrollPosition'
import {
  BLACK_MASS,
  ESG,
  FORECASTS,
  MARKET,
  MARKET_ASSESSMENTS,
  PRICE_ASSESSMENTS,
} from '~/constants/services/constants'
import type { TRecord, TTab } from '~/src/types/common'

const props = defineProps<{
  product: TRecord
  productData: TRecord
  forecastSubscription?: string
  esgSubscription?: string
  pricesAssessmentSubscription?: string
  marketAssessmentSubscription?: string
  hidePriceAssessmentsTab?: boolean
  showLatestReportSectionInPriceAssessments?: boolean
  showLatestReportSectionInMarketAssessments?: boolean
}>()

const { scrollToPosition } = useScrollPosition()

const activeTab = ref('overview')

const titlePage = computed(() => `${props.product.name} Data & Reports`)
const icon = computed(() => props.product.iconColor)
const heroImage = props.productData.hero
const initialFields = computed(() => [props.product.slug, MARKET.slug])

const tabs = computed((): TTab[] => {
  const { slug: productSlug, serviceBasePath } = props.product
  const baseSlug =
    productSlug === BLACK_MASS.slug
      ? serviceBasePath
      : (props.product.alternativeSlug ?? productSlug)
  const baseUrl = `/${baseSlug}/data-reports`

  const commonTabs = [
    !props.hidePriceAssessmentsTab && {
      name: PRICE_ASSESSMENTS.name,
      slug: PRICE_ASSESSMENTS.slug,
      order: 1,
    },
    props.marketAssessmentSubscription && {
      name: MARKET_ASSESSMENTS.name,
      slug: MARKET_ASSESSMENTS.slug,
      order: 2,
    },
    {
      name: FORECASTS.name,
      slug: 'forecasts',
      order: 3,
    },
    props.esgSubscription && {
      name: ESG.name,
      slug: ESG.singularSlug,
      order: 4,
    },
  ].filter(Boolean)

  return commonTabs
    .map((tab) => ({
      ...tab,
      url: `${baseUrl}/${tab.slug}`,
    }))
    .sort((a, b) => a.order - b.order)
})

const downloadText = computed(() => {
  const tabTextMap = {
    forecasts: 'Forecast Brochure',
    sustainability: 'Sustainability Brochure',
  }

  return tabTextMap[activeTab.value] || 'Methodology'
})

const file = computed(() => {
  const config = useRuntimeConfig()

  const tabFileMap = {
    'price-assessments': props.productData.priceAssessmentsBrochureFile,
    'market-assessments': props.productData.marketAssessmentsBrochureFile,
    forecast: props.productData.forecastBrochureFile,
    forecasts: props.productData.forecastBrochureFile,
    sustainability: props.productData.esgBrochureFile,
  }

  const selectedFile = tabFileMap[activeTab.value]

  if (selectedFile) {
    return {
      ...selectedFile,
      id: selectedFile.EnvKey ? config.public[selectedFile.EnvKey] : '',
    }
  }

  return {}
})

function setActiveTab(tab: string) {
  activeTab.value = tab
}

const route = useRoute()

watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab) {
      setTimeout(
        () => document.querySelector<HTMLButtonElement>(`#${newTab}`)?.click(),
        100,
      )
    }
  },
)

onMounted(() => {
  scrollToPosition()
})
</script>
